import PropTypes from 'prop-types';
import { useCallback, useEffect, useState, Fragment, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Card,
  Container,
  Stack,
  Tab,
  Tabs,
  tableCellClasses,
  Box,
  Typography,
  Grid,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import { useDispatch } from 'react-redux';

import EventNoteIcon from '@mui/icons-material/EventNote';
import EventIcon from '@mui/icons-material/Event';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GroupIcon from '@mui/icons-material/Group';
import { MdOutlineDateRange } from 'react-icons/md';

import { styled } from '@mui/material/styles';
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useDataSourceSelector from '../../store/Slice/DataSource/DataSourceSelector';
import {
  getDataSourceValidate,
  getDataSourceView
} from '../../store/Slice/DataSource/DataSourceSlice';
import Label from '../../components/Label';
import { fDateNew } from '../../utils/formatTime';

const STATIC_TABS = [
  { id: 1, label: 'View Dataset' },
  { id: 2, label: 'Valid Data Report' },
  { id: 3, label: 'Check Bureau Report' }
];

const getLabelColor = (s) =>
  ({
    New: 'info',
    Contacted: 'info',
    DUPLICATE: 'warning',
    NEGATIVE: 'error',
    INVALID: 'error',
    DND: 'warning',
    Completed: 'success',
    ACTIVE: 'success',
    Submitted: 'success'
  }[s] || 'warning');

function InfoRow({ label, value }) {
  return (
    <Box className="info-row">
      <Typography className="info-label">{label}</Typography>
      <Typography className="info-value">{value}</Typography>
    </Box>
  );
}

// -------------------------------------------------------------

export default function ViewDataSet() {
  const [fileTypeId, setFileTypeId] = useState(1);

  const { DataSourceViewData, DataSourceValidateData } = useDataSourceSelector();
  const location = useLocation();
  const { rowId } = location.state || {};
  const dispatch = useDispatch();

  const handleChangeTab = useCallback((_, newValue) => {
    setFileTypeId(newValue);
  }, []);

  const getViewDataSourceListApi = useCallback(() => {
    dispatch(getDataSourceView({ rowId }));
  }, [dispatch, rowId]);

  useEffect(() => {
    getViewDataSourceListApi();
  }, [getViewDataSourceListApi]);

  const getDataSourceValidateApi = useCallback(() => {
    dispatch(getDataSourceValidate({ rowId }));
  }, [dispatch, rowId]);

  useEffect(() => {
    getDataSourceValidateApi();
  }, [getDataSourceValidateApi]);

  const validRecords = useMemo(
    () => Number(DataSourceValidateData?.validRecords || 0),
    [DataSourceValidateData]
  );
  const totalRecords = useMemo(
    () => Number(DataSourceValidateData?.totalRecords || 0),
    [DataSourceValidateData]
  );

  return (
    <>
      <style>
        {`
          .description-box {
            margin-top: 8px;
            padding: 16px;
            border: 1px solid #E3E8EF;
            border-radius: 8px;
            background: #F7FAFC;
          }
             .description-box-passed {
            margin-top: 8px;
            padding: 16px;
             border: 1px solid rgb(187, 247, 208);
            border-radius: 8px;
            background-color: rgb(240, 253, 244);
          }
            .description-box-warning {
            margin-top: 8px;
            padding: 16px;
            border: 1px solid rgb(252, 211, 77);
            border-radius: 8px;
           background-color: rgb(254, 243, 199);
          }

          .summary-card {
            padding: 20px;
            background: #F8FAFC;
            border: 1px solid #E2E8F0;
            border-radius: 12px;
          }

          .card-title {
            font-weight: 700;
            margin-bottom: 12px;
          }

          .info-row {
            display: flex;
            justify-content: space-between;
            padding: 10px 0;
            border-bottom: 1px dashed #D4D8E1;
          }

          .info-row:last-child {
            border-bottom: none;
          }

          .info-label {
            color: #4A5568;
          }

          .info-value {
            font-weight: 600;
          }
        `}
      </style>

      <Page title="Partner Management System || Data Source">
        <Container maxWidth="xl">
          <Box pt={3} pb={3} mb={4}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              className="pageHeading"
            >
              <PageTitle info="View Dataset" />
              <Box>
                <BrudCrumbs />
              </Box>
            </Stack>

            {/* Tabs Header */}
            <Tabs
              value={fileTypeId}
              onChange={handleChangeTab}
              className="tabOuter"
              classes={{ indicator: 'tabIndicator' }}
            >
              {STATIC_TABS.map((tab) => (
                <Tab
                  key={tab.id}
                  value={tab.id}
                  label={tab.label}
                  classes={{ selected: 'tabSelected', root: 'tabDefault' }}
                />
              ))}
            </Tabs>

            {/* -------- TAB PANEL : VIEW DATASET -------- */}
            <TabPanel value={fileTypeId} index={1} style={{ paddingBottom: '100px' }}>
              <Typography
                style={{ marginTop: '30px' }}
                variant="body1"
                className="headingLineUp headingLineUpOut"
              >
                Dataset Information
              </Typography>

              <Box className="viewLeadCard cardTopMinusMargin" mt={5} mb={3}>
                <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} marginBottom={3}>
                  {/* Dataset fields */}
                  <Grid item xs={12} sm={6} md={4}>
                    <Box className="displayFlex">
                      <EventNoteIcon className="viewLabelIcon" />
                      <Box ml={1}>
                        <Typography className="labelValue">Dataset Name</Typography>
                        <Typography className="labelText">
                          {DataSourceViewData?.datasetInfo?.datasetName || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Box className="displayFlex">
                      <EventNoteIcon className="viewLabelIcon" />
                      <Box ml={1}>
                        <Typography className="labelValue">Import Method</Typography>
                        <Typography className="labelText">
                          {DataSourceViewData?.datasetInfo?.importMethod || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Box className="displayFlex">
                      <EventIcon className="viewLabelIcon" />
                      <Box ml={1}>
                        <Typography className="labelValue">Total Records</Typography>
                        <Typography className="labelText">
                          {DataSourceViewData?.datasetInfo?.totalRecords || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Box className="displayFlex">
                      <CalendarTodayIcon className="viewLabelIcon" />
                      <Box ml={1}>
                        <Typography className="labelValue">Total Columns</Typography>
                        <Typography className="labelText">
                          {DataSourceViewData?.datasetInfo?.totalColumns || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Box className="displayFlex">
                      <GroupIcon className="viewLabelIcon" />
                      <Box ml={1}>
                        <Typography className="labelValue">Uploaded On</Typography>
                        <Typography className="labelText">
                          {fDateNew(DataSourceViewData?.datasetInfo?.uploadedOn) || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <Box className="displayFlex">
                      <MdOutlineDateRange className="viewLabelIcon" />
                      <Box ml={1}>
                        <Typography className="labelValue">Modified On</Typography>
                        <Typography className="labelText">
                          {fDateNew(DataSourceViewData?.datasetInfo?.modifiedOn) || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                {/* Description Box */}
                <Paper elevation={0} className="description-box">
                  <Typography variant="caption">Description</Typography>
                  <Typography variant="body2">
                    {DataSourceViewData?.datasetInfo?.datasetDescription}
                  </Typography>
                </Paper>
              </Box>

              <Typography variant="body1" className="headingLineUp headingLineUpOut">
                File Details
              </Typography>
              {/* Summary Cards */}
              <Box className="viewLeadCard cardTopMinusMargin" mt={5} mb={3}>
                <Box className="data-summary-container">
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Paper className="summary-card">
                        <Typography className="card-title">Source File</Typography>

                        <InfoRow
                          label="File Name"
                          value={DataSourceViewData?.fileDetails?.fileName}
                        />
                        <InfoRow
                          label="File Size"
                          value={DataSourceViewData?.fileDetails?.fileSizeMb}
                        />
                        <InfoRow
                          label="Total Rows in File"
                          value={DataSourceViewData?.fileDetails?.totalRowsInFile}
                        />
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper className="summary-card">
                        <Typography className="card-title">Import Summary</Typography>

                        <InfoRow
                          label="Imported Records"
                          value={DataSourceViewData?.importSummary?.importedRecords}
                        />
                        <InfoRow
                          label="Rejected Rows"
                          value={DataSourceViewData?.importSummary?.rejectedRows}
                        />
                        <InfoRow
                          label="Duplicate Records"
                          value={DataSourceViewData?.importSummary?.duplicateRecords}
                        />
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              </Box>

              <Typography variant="body1" className="headingLineUp headingLineUpOut">
                Column Structure
              </Typography>
              {/* Summary Cards */}
              <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
                <Box p={2}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <b>COLUMN NAME</b>
                          </TableCell>
                          <TableCell>
                            <b>DATA TYPE</b>
                          </TableCell>
                          <TableCell>
                            <b>SAMPLE DATA</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {DataSourceViewData?.columnStructure
                          ?.filter(
                            (item) =>
                              item?.columnName !== 'company_id' &&
                              item?.columnName !== 'created_by' &&
                              item?.columnName !== 'loan_type_id'
                          )
                          ?.map((row) => (
                            <TableRow key={row.columnName}>
                              <TableCell>{row.columnName}</TableCell>
                              <TableCell>
                                <Label variant="ghost" color={getLabelColor('new')}>
                                  {row.dataType}
                                </Label>
                              </TableCell>
                              <TableCell>{row.sampleValue}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>

              <Typography variant="body1" className="headingLineUp headingLineUpOut">
                Record List
              </Typography>
              {/* Summary Cards */}
              <Box className="viewLeadCard cardTopMinusMargin" mb={3}>
                <Box p={2}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <b>ID</b>
                          </TableCell>
                          <TableCell>
                            <b>Name</b>
                          </TableCell>
                          <TableCell>
                            <b>Phone</b>
                          </TableCell>
                          <TableCell>
                            <b>Email</b>
                          </TableCell>
                          <TableCell>
                            <b>Loan Type</b>
                          </TableCell>

                          <TableCell>
                            <b> Amount</b>
                          </TableCell>
                          <TableCell>
                            <b> City</b>
                          </TableCell>
                          <TableCell>
                            <b> Status</b>
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {DataSourceViewData?.sampleRecords?.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.id}</TableCell>
                            <TableCell>{row?.data?.name}</TableCell>
                            <TableCell>{row?.data?.phone}</TableCell>
                            <TableCell>{row?.data?.email}</TableCell>
                            <TableCell>{row?.data?.loan_type}</TableCell>
                            <TableCell>{row?.data?.loan_amount}</TableCell>
                            <TableCell>{row?.data?.city}</TableCell>
                            <TableCell>
                              <Label variant="ghost" color={getLabelColor(row?.recordStatus)}>
                                {row?.recordStatus}
                              </Label>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>
            </TabPanel>

            {/* -------- OTHER TABS -------- */}

            <TabPanel value={fileTypeId} index={2}>
              <Typography
                style={{ marginTop: '30px' }}
                variant="body1"
                className="headingLineUp headingLineUpOut"
              >
                Data Validation Report
              </Typography>

              <Box className="viewLeadCard cardTopMinusMargin" mt={5} mb={3}>
                <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} marginBottom={3}>
                  {/* Dataset fields */}
                  <Grid item xs={12} sm={12} md={12}>
                    <Paper elevation={0} className="description-box-passed">
                      <Typography
                        variant="h6"
                        mb={1}
                        sx={{ color: 'success.dark', fontWeight: 700 }}
                      >
                        ✓ Validation Status: PASSED{' '}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'success.dark' }}>
                        All records have been successfully validated against the required criteria.
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Paper elevation={0} className="description-box">
                      <Typography variant="caption">Total Records</Typography>
                      <Typography variant="h6">{DataSourceValidateData?.totalRecords}</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Paper elevation={0} className="description-box">
                      <Typography variant="caption">Valid Records</Typography>
                      <Typography variant="h6" sx={{ color: 'success.dark', fontWeight: 700 }}>
                        {DataSourceValidateData?.validRecords}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Paper elevation={0} className="description-box">
                      <Typography variant="caption">Invalid Records</Typography>
                      <Typography variant="h6" sx={{ color: 'error.main' }}>
                        {DataSourceValidateData?.invalidRecords}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Paper elevation={0} className="description-box">
                      <Typography variant="caption">Pass Rate</Typography>
                      <Typography variant="h6" sx={{ color: 'success.dark', fontWeight: 700 }}>
                        {totalRecords > 0
                          ? ((validRecords / totalRecords) * 100).toFixed(0)
                          : '0.00'}
                        %
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12}>
                    <Paper elevation={0} className="description-box">
                      <Typography subtitle2 mb={1}>
                        <b> Validation Rules Checked:</b>
                      </Typography>
                      <Typography className="info-label">✓ Required fields are present </Typography>
                      <Typography className="info-label">
                        ✓ Email format validation passed{' '}
                      </Typography>
                      <Typography className="info-label">
                        ✓ Phone number format is valid{' '}
                      </Typography>
                      <Typography className="info-label">
                        ✓ Loan amount is within valid range{' '}
                      </Typography>
                      <Typography className="info-label">✓ No duplicate records found </Typography>
                    </Paper>
                  </Grid>
                </Grid>
                {/* Description Box */}
              </Box>
            </TabPanel>

            <TabPanel value={fileTypeId} index={3}>
              <Typography
                style={{ marginTop: '30px' }}
                variant="body1"
                className="headingLineUp headingLineUpOut"
              >
                Bureau Check Report
              </Typography>

              <Box className="viewLeadCard cardTopMinusMargin" mt={5} mb={3}>
                <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} marginBottom={3}>
                  {/* Dataset fields */}
                  <Grid item xs={12} sm={12} md={12}>
                    <Paper elevation={0} className="description-box-warning">
                      <Typography variant="h6" mb={1} sx={{ color: 'warning.dark' }}>
                        ⚠ Bureau Check Status: IN PROGRESS
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'warning.dark' }}>
                        Bureau verification is being processed. This may take 24-48 hours to
                        complete.
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Paper elevation={0} className="description-box">
                      <Typography variant="caption">Records Submitted</Typography>
                      <Typography variant="h6">1,250</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Paper elevation={0} className="description-box">
                      <Typography variant="caption">Bureau Match Found</Typography>
                      <Typography variant="h6" sx={{ color: 'success.dark', fontWeight: 700 }}>
                        892
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Paper elevation={0} className="description-box">
                      <Typography variant="caption">No Match</Typography>
                      <Typography variant="h6" sx={{ color: 'warning.main' }}>
                        358
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <Paper elevation={0} className="description-box">
                      <Typography variant="caption">Match Rate</Typography>
                      <Typography variant="h6" sx={{ color: 'success.dark', fontWeight: 700 }}>
                        71.4%
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={12} md={12}>
                    <Paper elevation={0} className="description-box">
                      <Typography subtitle2 mb={1}>
                        <b> Bureau Check Details:</b>
                      </Typography>
                      <Typography className="info-label">
                        ✓ Credit Bureau: CIBIL (Credit Information Bureau India Limited){' '}
                      </Typography>
                      <Typography className="info-label">
                        ✓ Query Submission Date: 24 Dec 2024
                      </Typography>
                      <Typography className="info-label">
                        ✓ Expected Completion: 26 Dec 2024
                      </Typography>
                      <Typography className="info-label">
                        ✓ Average Credit Score (Matched): 680
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
                {/* Description Box */}
              </Box>
            </TabPanel>
          </Box>
        </Container>
      </Page>
    </>
  );
}

// -------------------------------------------------------------

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box className="tab-panel-box">{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired
};
